import React, { useState, useEffect, useRef } from "react";
import { initializeApp,getApps,getApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
  where,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {db,storage} from "./firebase";

// ===== Replace with your Firebase config =====


const EMOJIS = ["😀", "😂", "😍", "😘", "😢", "😡", "👍", "🙏", "🔥", "💖", "🎉", "🌹", "🍕", "☕️", "🎵"];
const DEFAULT_INTERESTS = ["Music", "Movies", "Cooking", "Travel", "Gaming", "Books", "Fitness", "Art"];

export default function App() {
  const [name, setName] = useState("");
  const [gender, setGender] = useState("female");
  const [interests, setInterests] = useState([]);
  const [customInterest, setCustomInterest] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [joined, setJoined] = useState(false);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [matchInfo, setMatchInfo] = useState(null);
  const [error, setError] = useState("");

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const waitingDocId = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Listen for messages
  useEffect(() => {
    if (!joined) return;
    const messagesRef = collection(db, "rooms", roomCode, "messages");
    const q = query(messagesRef, orderBy("createdAt"));
    const unsub = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => doc.data());
      setMessages(msgs);
    });
    return () => unsub();
  }, [joined, roomCode]);

  // Real-time matchmaking listener
  useEffect(() => {
    if (!waiting) return;

    const roomRef = collection(db, "rooms");
    const q = query(roomRef, where("users", "array-contains", name));

    const unsubscribe = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type === "added") {
          const roomData = change.doc.data();
          if (!joined) {
            const otherUser = roomData.users.find((u) => u !== name);
            setRoomCode(roomData.code);
            setJoined(true);
            setWaiting(false);

            setMatchInfo({
              name: otherUser,
              genderIcon: "🧑", // optional: fetch real gender from user profile
              sharedInterests: [], // optional: store shared interests in room doc
            });
          }
        }
      });
    });

    return () => unsubscribe();
  }, [waiting, name, joined]);

  const toggleInterest = (interest) => {
    setInterests((prev) =>
      prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest]
    );
    if (error) setError("");
  };

  const addCustomInterest = () => {
    const trimmed = customInterest.trim();
    if (!trimmed) return;
    if (!interests.includes(trimmed)) {
      setInterests((prev) => [...prev, trimmed]);
    }
    setCustomInterest("");
    if (error) setError("");
  };

  const findMatch = async () => {
    setWaiting(true);

    const waitingRef = collection(db, "waitingUsers");
    const q = query(waitingRef, orderBy("timestamp"));
    const snap = await getDocs(q);

    let matched = false;

    for (const docSnap of snap.docs) {
      const data = docSnap.data();
      if (data.name === name) continue;

      const shared = data.interests.filter((i) => interests.includes(i));
      if (shared.length > 0) {
        // Match found
        const roomCode = Math.random().toString(36).substring(2, 8);

        // Create room doc
        await setDoc(doc(db, "rooms", roomCode), {
          code: roomCode,
          users: [data.name, name],
          createdAt: serverTimestamp(),
        });

        // Remove matched user from waitingUsers
        await deleteDoc(doc(db, "waitingUsers", docSnap.id));

        // Update state for self
        setRoomCode(roomCode);
        setJoined(true);
        setWaiting(false);

        const genderIcon =
          data.gender === "male"
            ? "👨"
            : data.gender === "female"
            ? "👩"
            : "🧑";

        setMatchInfo({
          name: data.name,
          genderIcon,
          sharedInterests: shared,
        });

        matched = true;
        break;
      }
    }

    if (!matched) {
      // Add self to waitingUsers
      await setDoc(doc(db, "waitingUsers", name), {
        name,
        interests,
        gender,
        timestamp: serverTimestamp(),
      });
      waitingDocId.current = name;
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    const messagesRef = collection(db, "rooms", roomCode, "messages");
    await addDoc(messagesRef, {
      text: message.trim(),
      createdAt: serverTimestamp(),
      senderName: name,
      senderGender: gender,
      imageUrl: null,
    });
    setMessage("");
    setEmojiPickerOpen(false);
  };

  const sendImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const imgRef = storageRef(
        storage,
        `chatImages/${roomCode}/${Date.now()}_${file.name}`
      );
      await uploadBytes(imgRef, file);
      const url = await getDownloadURL(imgRef);
      const messagesRef = collection(db, "rooms", roomCode, "messages");
      await addDoc(messagesRef, {
        text: "",
        createdAt: serverTimestamp(),
        senderName: name,
        senderGender: gender,
        imageUrl: url,
      });
    } catch (err) {
      setError("Upload failed: " + err.message);
    }
    setUploading(false);
  };

  const disconnect = async () => {
    if (waitingDocId.current)
      await deleteDoc(doc(db, "waitingUsers", waitingDocId.current)).catch(() => {});
    setJoined(false);
    setWaiting(false);
    setRoomCode("");
    setMessages([]);
    setMessage("");
    setName("");
    setGender("female");
    setInterests([]);
    setEmojiPickerOpen(false);
    setMatchInfo(null);
    setError("");
  };

  // ========= JOIN SCREEN =========
  if (!joined) {
    return (
      <div
        style={{
          maxWidth: 420,
          margin: "3rem auto",
          fontFamily: "'Segoe UI', sans-serif",
          padding: "1.5rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: "10px",
          backgroundColor: "#fff",
        }}
      >
        <h1 style={{ textAlign: "center", color: "#4a148c" }}>💞 Matchmaking Chat</h1>
        {/* Name Input */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ fontWeight: 600 }}>Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError("");
            }}
            style={{
              width: "100%",
              padding: "0.6rem",
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />
        </div>
        {/* Gender Select */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ fontWeight: 600 }}>Gender:</label>
          <select
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              if (error) setError("");
            }}
            style={{
              width: "100%",
              padding: "0.6rem",
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          >
            <option value="female">Female 👩</option>
            <option value="male">Male 👨</option>
            <option value="other">Other 🧑</option>
          </select>
        </div>
        {/* Interests */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ fontWeight: 600 }}>Select Interests:</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
            {DEFAULT_INTERESTS.map((int) => (
              <button
                key={int}
                onClick={() => toggleInterest(int)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "12px",
                  border: interests.includes(int) ? "2px solid #6a1b9a" : "1px solid #ccc",
                  backgroundColor: interests.includes(int) ? "#ede7f6" : "#fff",
                  cursor: "pointer",
                }}
              >
                {int}
              </button>
            ))}
          </div>
        </div>
        {/* Custom Interest */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "1rem" }}>
          <input
            type="text"
            placeholder="Add custom interest..."
            value={customInterest}
            onChange={(e) => setCustomInterest(e.target.value)}
            style={{
              flexGrow: 1,
              padding: "0.6rem",
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />
          <button
            onClick={addCustomInterest}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: 6,
              border: "none",
              backgroundColor: "#6a1b9a",
              color: "white",
              cursor: "pointer",
            }}
          >
            ➕
          </button>
        </div>
        {/* Error & Waiting */}
        {error && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.7rem",
              backgroundColor: "#ffebee",
              borderRadius: "8px",
              textAlign: "center",
              color: "#d32f2f",
              fontWeight: 600,
            }}
          >
            ⚠️ {error}
          </div>
        )}
        {waiting && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.7rem",
              backgroundColor: "#f3e5f5",
              borderRadius: "8px",
              textAlign: "center",
              color: "#6a1b9a",
            }}
          >
            ⏳ Searching for someone with shared interests...
          </div>
        )}
        {/* Selected Interests */}
        <div style={{ marginBottom: "1rem" }}>
          <strong>Your Interests:</strong>{" "}
          {interests.length > 0 ? interests.join(", ") : "(none selected yet)"}
        </div>
        {/* Find Match */}
        <button
          onClick={findMatch}
          disabled={waiting}
          style={{
            width: "100%",
            padding: "0.7rem",
            fontSize: "1.1rem",
            backgroundColor: waiting ? "#aaa" : "#6a1b9a",
            color: "white",
            border: "none",
            cursor: "pointer",
            borderRadius: "6px",
            boxShadow: "0 4px 6px rgba(106,27,154,0.4)",
          }}
        >
          {waiting ? "Searching..." : "Find Match 💞"}
        </button>
      </div>
    );
  }

  // ========= CHAT SCREEN =========
  return (
    <div
      style={{
        maxWidth: 700,
        margin: "2rem auto",
        fontFamily: "'Segoe UI', sans-serif",
        padding: "1rem",
        border: "1px solid #ddd",
        borderRadius: 12,
        boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        height: "80vh",
      }}
    >
      {/* Matched Info */}
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        {matchInfo && (
          <div
            style={{
              backgroundColor: "#ede7f6",
              padding: "0.7rem",
              borderRadius: "8px",
              color: "#4a148c",
              fontWeight: 600,
            }}
          >
            💞 Matched with {matchInfo.genderIcon} {matchInfo.name}  
            <br />
            <small>
              Shared Interests: {matchInfo.sharedInterests.join(", ")}
            </small>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
        <h3 style={{ color: "#4a148c" }}>Room: {roomCode}</h3>
        <button
          onClick={disconnect}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#d32f2f",
            color: "white",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Disconnect
        </button>
      </div>

      {/* Messages */}
      <div
        style={{
          flexGrow: 1,
          border: "1px solid #ccc",
          overflowY: "auto",
          padding: "1rem",
          backgroundColor: "#fafafa",
          borderRadius: 8,
          marginBottom: "1rem",
        }}
      >
        {messages.length === 0 && (
          <p style={{ textAlign: "center", color: "#888" }}>No messages yet</p>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.senderName === name;
          const genderIcon =
            msg.senderGender === "male"
              ? "👨"
              : msg.senderGender === "female"
              ? "👩"
              : "🧑";
          return (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: isMe ? "flex-end" : "flex-start",
                marginBottom: "0.8rem",
              }}
            >
              <div
                style={{
                  maxWidth: "70%",
                  backgroundColor: isMe ? "#d1c4e9" : "#f8bbd0",
                  padding: "0.8rem 1rem",
                  borderRadius: isMe ? "20px 20px 0 20px" : "20px 20px 20px 0",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.3rem" }}>
                  {genderIcon} {msg.senderName}
                </div>
                {msg.text && (
                  <div style={{ whiteSpace: "pre-wrap", fontSize: "1.1rem" }}>
                    {msg.text}
                  </div>
                )}
                {msg.imageUrl && (
                  <img
                    src={msg.imageUrl}
                    alt="shared"
                    style={{
                      maxWidth: "100%",
                      marginTop: "8px",
                      borderRadius: "12px",
                      cursor: "pointer",
                    }}
                    onClick={() => window.open(msg.imageUrl, "_blank")}
                  />
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <button
          onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
          style={{
            fontSize: "1.8rem",
            background: "none",
            border: "none",
            cursor: "pointer",
            marginRight: "8px",
          }}
        >
          😊
        </button>

        {emojiPickerOpen && (
          <div
            style={{
              position: "absolute",
              bottom: "70px",
              left: "20px",
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "12px",
              padding: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              display: "flex",
              flexWrap: "wrap",
              width: "260px",
              zIndex: 100,
            }}
          >
            {EMOJIS.map((emoji, idx) => (
              <span
                key={idx}
                style={{ fontSize: "1.5rem", padding: "6px", cursor: "pointer" }}
                onClick={() => setMessage((m) => m + emoji)}
              >
                {emoji}
              </span>
            ))}
          </div>
        )}

        <input
          type="text"
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{
            flexGrow: 1,
            padding: "0.7rem 1rem",
            borderRadius: 20,
            border: "1px solid #ccc",
            marginRight: "6px",
          }}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />
        <button
          onClick={sendMessage}
          style={{
            padding: "0.7rem 1rem",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "#6a1b9a",
            color: "white",
            cursor: "pointer",
          }}
        >
          ➤
        </button>
        <button
          onClick={() => fileInputRef.current.click()}
          style={{
            padding: "0.7rem 1rem",
            marginLeft: "6px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "#4a148c",
            color: "white",
            cursor: "pointer",
          }}
        >
          📎
        </button>
        <input
          type="file"
          style={{ display: "none" }}
          ref={fileInputRef}
          accept="image/*"
          onChange={(e) => sendImage(e.target.files[0])}
        />
      </div>

      {uploading && (
        <div style={{ marginTop: "0.5rem", color: "#6a1b9a" }}>Uploading image...</div>
      )}
    </div>
  );
}
